import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { ResponseError } from '@wisenet/salesapp-client'
import { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useParams } from 'react-router-dom'
import { formSubmissionApi, siteApi } from '../../api'
import loader from '../../assets/loader.svg'
import { StripePayment } from '../../components/payment/StripePayment'
import { StyledButton } from '../../elements/Button'
import { CustomLink } from '../../elements/CustomLink'
import { Loader } from '../../elements/Loader'
import { Nullable, useAppSelector } from '../../types/assets'
import { PaygateRedirectForm, PaymentGrid, StyledPaymentContainer } from './styles'

const stripeReference: any = {}

export const PaymentForm = (props: any) => {
  const [amount, setAmount] = useState<Nullable<number>>()
  const [currency, setCurrency] = useState<Nullable<string>>()
  const [errorMessage, setErrorMessage] = useState('')
  const [paid, setPaid] = useState(false)
  const [clientSecret, setClientSecret] = useState<Nullable<string>>()
  const [, setStripePublicKey] = useState<Nullable<string>>()
  const [isPaymentDemoMode, setIsPaymentDemoMode] = useState<Nullable<boolean>>()
  const [intakeName, setIntakeName] = useState<Nullable<string>>()
  const { id } = useParams()

  const [paygatePayRequestId, setPaygatePayRequestId] = useState<Nullable<string>>()
  const [paygateChecksum, setPaygateChecksum] = useState<Nullable<string>>()
  const [paymentGateway, setPaymentGateway] = useState<Nullable<string>>()

  const appearance = {
    theme: 'stripe',
  }

  const options: any = {
    clientSecret,
    appearance,
  }

  const state = useAppSelector(state => state)
  const {
    settings: {
      config: { path },
    },
  } = state

  var submitButton: any

  useEffect(() => {
    id &&
      siteApi
        .getSitePaymentInfo()
        .then(res => {
          setIsPaymentDemoMode(res?.isPaymentDemoMode)
          if (res?.paymentGateway?.toLowerCase() === 'stripe') {
            stripeReference.stripePromise = res?.stripePublicKey ? loadStripe(res?.stripePublicKey) : null
            setStripePublicKey(res?.stripePublicKey)
          }
          setPaymentGateway(res?.paymentGateway)
        })
        .catch(e => {
          return e.response?.json ? e.response.json() : e.response
        })
        .then((error: any) => {
          setErrorMessage(error?.Message ?? JSON.stringify(error))
        })
  }, [id])

  useEffect(() => {
    id &&
      paymentGateway &&
      formSubmissionApi
        .createPaymentIntent({ guid: id })
        .then(res => {
          setAmount(res?.paymentAmount || 0)
          setCurrency(res?.currencySymbol || '')
          setClientSecret(res?.reference)
          setIntakeName(res?.description)

          if (res?.checksum && res?.id) {
            //Paygate
            setPaygatePayRequestId(res.id)
            setPaygateChecksum(res.checksum)
            return
          }
          setClientSecret(res?.reference)
        })
        .catch((e: ResponseError) => {
          setErrorMessage(e.message)
          setIntakeName(e.data?.description ?? ' ')
        })
  }, [paymentGateway])

  // Auto submit for Paygate payment
  useEffect(() => {
    if (paymentGateway?.toLowerCase() === 'paygate') submitButton && submitButton.click()
  })

  const hideTryAgain = errorMessage?.includes('expired') || errorMessage?.includes('failed')
  if (errorMessage) {
    return (
      <StyledPaymentContainer>
        <section className="payment-head">
          <h3> {intakeName || <Skeleton width="50%" />} </h3>
          <p></p>
          <label className="heading">Payment</label>
          <hr />
        </section>

        <section className="payment-body">
          <span className="error">{errorMessage}</span>
          <br />
          <br />
          <div>
            {!hideTryAgain && (
              <>
                <CustomLink
                  to={`/${path}/payments/${id}`}
                  label="Try again"
                />
                <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
              </>
            )}
            <CustomLink
              to={`/${path}/courses`}
              label="Back to website to look at more courses"
            />
          </div>
        </section>
      </StyledPaymentContainer>
    )
  }

  if (paymentGateway?.toLowerCase() === 'paygate' && paygateChecksum && paygatePayRequestId) {
    return (
      <PaygateRedirectForm>
        <form
          action="https://secure.paygate.co.za/payweb3/process.trans"
          method="POST"
        >
          <span>Redirect to Paygate payment page </span>
          <img
            src={loader}
            alt="Loader"
          />
          <input
            type="hidden"
            name="PAY_REQUEST_ID"
            value={paygatePayRequestId ?? ''}
          />
          <input
            type="hidden"
            name="CHECKSUM"
            value={paygateChecksum ?? ''}
          />
          <br />
          <StyledButton
            disabled={false}
            large={true}
          >
            <button
              ref={button => (submitButton = button)}
              type="submit"
              value="Submit"
            >
              Submit
            </button>
          </StyledButton>
        </form>
      </PaygateRedirectForm>
    )
  } else if (paymentGateway?.toLowerCase() === 'stripe') {
    return (
      <StyledPaymentContainer>
        <section className="payment-head">
          <h3> {intakeName || <Skeleton width="50%" />} </h3>
          <p>
            {intakeName ? (
              paid ? (
                'Your application is finalised.'
              ) : (
                'Your application is not finalised. Please make payment to finalise the submission.'
              )
            ) : (
              <Skeleton width="70%" />
            )}
          </p>
          <label className="heading">Payment</label>
          <hr />
        </section>

        <section className="payment-body">
          <PaymentGrid>
            <div className="panel">
              <label>
                Payment Amount:
                <span className="amount">
                  {currency} {amount?.toFixed(2) || <Skeleton width="70px" />}
                </span>
              </label>
            </div>
            <div className="panel">
              {clientSecret && stripeReference.stripePromise ? (
                <Elements
                  options={options}
                  stripe={stripeReference.stripePromise}
                >
                  <StripePayment
                    intakeName={intakeName}
                    clientSecret={clientSecret}
                    isPaymentDemoMode={isPaymentDemoMode}
                    setPaid={setPaid}
                  />
                </Elements>
              ) : (
                <div>
                  <p>
                    <Skeleton
                      width="100%"
                      height="30px"
                    />
                  </p>
                  <p>
                    <Skeleton
                      width="100%"
                      height="30px"
                    />
                  </p>
                  <p>
                    <Skeleton
                      width="100%"
                      height="30px"
                    />
                  </p>
                  <p>
                    <Skeleton
                      width="100%"
                      height="30px"
                    />
                  </p>
                  <p>
                    <Skeleton
                      width="100%"
                      height="30px"
                    />
                  </p>
                </div>
              )}
            </div>
          </PaymentGrid>
        </section>
      </StyledPaymentContainer>
    )
  }

  return <Loader />
}
